import { useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'next-i18next';
import { useSelector } from 'react-redux';

import { selectSimilarProductsByColorId } from '@selectors/products';

import { PRODUCT_TYPE } from '@common/constants/products';
import { productAllTypesShape } from '@common/types/product';
import Product from '@common/containers/Product';
import LayoutSwitcher from '@common/components/LayoutSwitcher';
import ProductStickyContainer from '@common/components/ProductStickyContainer';
import { ProductsGrid, ProductWrapper } from '@common/components/ProductsGrid';
import { useProductColorVariationsData } from '@common/hooks/useProductData';
import ShopEventProductBox from '@common/components/ShopEventProductBox';

import {
  RelatedProductsContainer,
  RelatedProductsTitle,
} from './RelatedProducts.styled';

const RelatedProducts = ({
  product,
  productType,
  stickyContainerBottomSpacing,
  productColorId,
}) => {
  const { t } = useTranslation();

  const { productId, relatedProductIds = [] } = product;
  const similarProductsIds = useSelector(
    selectSimilarProductsByColorId(productColorId),
  );
  const productsGridRef = useRef(null);

  const relatedProductColorVariations = useProductColorVariationsData({
    ids:
      productType === PRODUCT_TYPE.SHOP_EVENT
        ? relatedProductIds
        : similarProductsIds,
    type: productType,
  });

  if (!relatedProductColorVariations.length) return null;

  return (
    <RelatedProductsContainer>
      <RelatedProductsTitle>
        {t(
          'productDetails.relatedProductsTitle',
          'Das könnte Dir auch gefallen',
        )}
      </RelatedProductsTitle>
      <div>
        <ProductsGrid ref={productsGridRef}>
          {relatedProductColorVariations.map(
            ({
              discountPercentage = null,
              productColorId: relatedProductColorId,
              publishedUntil,
              availability,
              availabilityLabel,
              specialPrice,
              colorTitle,
              offerType,
              image,
              price,
              title,
              slug,
            }) => (
              <ProductWrapper key={relatedProductColorId}>
                {productType === PRODUCT_TYPE.DEFAULT ? (
                  <Product
                    productColorId={relatedProductColorId}
                    publishedUntil={publishedUntil}
                    specialPrice={specialPrice}
                    availability={availability}
                    colorTitle={colorTitle}
                    productId={productId}
                    offerType={offerType}
                    image={image}
                    price={price}
                    slug={slug}
                    title={title}
                  />
                ) : (
                  <ShopEventProductBox
                    image={image}
                    price={price}
                    productColorId={relatedProductColorId}
                    slug={slug}
                    specialPrice={specialPrice}
                    title={title}
                    availabilityLabel={availabilityLabel}
                    discountPercentage={discountPercentage}
                    colorTitle={colorTitle}
                  />
                )}
              </ProductWrapper>
            ),
          )}
        </ProductsGrid>
        <ProductStickyContainer
          bottomSpacing={stickyContainerBottomSpacing ? '7rem' : '0'}
          productsGridRef={productsGridRef}
        >
          <LayoutSwitcher />
        </ProductStickyContainer>
      </div>
    </RelatedProductsContainer>
  );
};

RelatedProducts.propTypes = {
  product: productAllTypesShape.isRequired,
  productType: PropTypes.oneOf([PRODUCT_TYPE.DEFAULT, PRODUCT_TYPE.SHOP_EVENT]),
  stickyContainerBottomSpacing: PropTypes.bool,
};

RelatedProducts.defaultProps = {
  productType: PRODUCT_TYPE.DEFAULT,
  stickyContainerBottomSpacing: false,
};

export default RelatedProducts;
